import React, { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  Text,
  Box,
  Button,
  Banner,
  Icon,
  List,
  LegacyStack,
} from '@shopify/polaris';
import { CodeIcon, ClipboardIcon } from '@shopify/polaris-icons';

const TRACKER = process.env.REACT_APP_TRACKER_URL;

export default function ConnectFailurePage() {
  const navigate = useNavigate();
  const location = useLocation();

  // Recover site from navigation state or sessionStorage
  const site = useMemo(() => {
    const fromNav = location.state?.site;
    if (fromNav) return fromNav;
    const pending = sessionStorage.getItem('pendingShopifySite');
    return pending ? JSON.parse(pending) : undefined;
  }, [location.state]);

  // If no site info, send back to dashboard
  useEffect(() => {
    if (!site) navigate('/dashboard');
  }, [site, navigate]);

  if (!site) return null;

  // Build script snippet
  const trackingScript = `<script src="${TRACKER}/tracker.js" data-site-id="${site.id}" data-site-token="${site.token}" data-host="${TRACKER}" data-sessions-path="/sessions.js"></script>`;

  const handleCopy = () => {
    navigator.clipboard.writeText(trackingScript).catch(console.error);
  };

  return (
    <Page title="Manual Installation Required">
      <Layout>
        <Layout.Section>
          <Card sectioned>
            <LegacyStack vertical>

              <Banner status="warning">
                We couldn’t automatically inject the tracking script—please follow these steps:
              </Banner>

              <Box>
                <Text as="p" variant="bodyMd" marginBlockEnd="8px">
                  To edit your <code>theme.liquid</code> file in Shopify:
                </Text>
                <List type="bullet">
                  <List.Item>
                    Log in to your Shopify Admin at <code>https://your-store.myshopify.com/admin</code>.
                  </List.Item>
                  <List.Item>
                    In the sidebar under Sales Channels, click <strong>Online Store</strong>.
                  </List.Item>
                  <List.Item>
                    Click <strong>Themes</strong> and find your published theme marked “Current.”
                  </List.Item>
                  <List.Item>
                    Click <strong>••• Actions</strong> on that theme and select <strong>Edit code</strong>.
                  </List.Item>
                  <List.Item>
                    In the editor sidebar, expand <strong>Layout</strong> and open <strong>theme.liquid</strong>.
                  </List.Item>
                  <List.Item>
                    Paste your snippet immediately above <code>&lt;/head&gt;</code>, then click <strong>Save</strong>.
                  </List.Item>
                </List>
              </Box>

              <LegacyStack vertical spacing="loose">
                {/* Step 1 */}
                <LegacyStack vertical>
                  <LegacyStack alignment="center">
                    <Icon source={CodeIcon} color="base" />
                    <Text as="h3" variant="headingMd">
                      1. Copy your unique tracking script
                    </Text>
                  </LegacyStack>
                  <Box border="base" background="surfaceSubdued" padding="4" borderRadius="4" style={{ position: 'relative', overflowX: 'auto' }}>
                    <pre style={{ margin: 0, fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                      {trackingScript}
                    </pre>
                    <Button plain icon={ClipboardIcon} onClick={handleCopy} style={{ position: 'absolute', top: 8, right: 8 }}>
                      Copy
                    </Button>
                  </Box>
                </LegacyStack>

                {/* Step 2 */}
                <LegacyStack vertical>
                  <Text as="h3" variant="headingMd">
                    2. Paste into your HTML
                  </Text>
                  <Text as="p" variant="bodyMd" tone="subdued">
                    Insert immediately before the closing <code>&lt;/head&gt;</code> tag:
                  </Text>
                  <Box border="base" background="surface" padding="4" borderRadius="4" style={{ overflowX: 'auto' }}>
                    <pre style={{ margin: 0, fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
{`<head>
  <!-- existing head content -->
  ${trackingScript}
</head>`}
                    </pre>
                  </Box>
                </LegacyStack>

                {/* Step 3 */}
                <LegacyStack vertical>
                  <Text as="h3" variant="headingMd">
                    3. Verify it’s working
                  </Text>
                  <List type="number">
                    <List.Item>
                      See <code>tracker.js</code> load in DevTools Network.
                    </List.Item>
                    <List.Item>
                      Confirm your <code>/api/collect</code> POSTs return 200.
                    </List.Item>
                  </List>
                </LegacyStack>
              </LegacyStack>

              <Banner status="info" title="Insights may take a few days to appear" marginBlockStart="loose">
                BaoBrain needs visitor traffic to build heatmaps, session paths, and root‑cause insights.
              </Banner>

              <Button primary fullWidth onClick={() => navigate('/dashboard')}>
                Go to Dashboard
              </Button>
            </LegacyStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
